import {
  CheckCircle,
  Clock,
  DollarSign,
  Globe,
  Smartphone,
  FileTextIcon,
  BriefcaseIcon,
  Globe2Icon,
  UserIcon,
  TargetIcon,
} from 'lucide-react'
import type { ToolLandingConfig } from '../_components/tool-landing-template'

export const ai_value_proposition_generatorConfig: ToolLandingConfig = {
  hero: {
    eyebrow: 'Free AI Tool · Professional Results',
    title: 'Free AI Ai Value Proposition Generator by HeyBoss',
    description:
      'Create professional ai value proposition generator with HeyBoss AI. Industry-leading quality, lightning-fast results, optimized for business success.',
    bullets: [
      'AI-powered professional quality',
      'Industry-specific optimization',
      'Results-driven approach',
      'Complete customization control',
    ],
    ctaText: 'Start Creating Now',
  },
  officialDefinition:
    'Free AI Ai Value Proposition Generator by HeyBoss - Professional ai value proposition generator solution with AI-powered features. Optimized for businesses worldwide.',
  steps: [
    {
      number: '1',
      title: 'Input Your Requirements',
      description: 'Tell us about your project, goals, and preferences.',
    },
    {
      number: '2',
      title: 'AI Generates Content',
      description:
        'Advanced AI creates professional content tailored to your needs.',
    },
    {
      number: '3',
      title: 'Review & Customize',
      description: 'Edit, refine, and perfect your content before publishing.',
    },
  ],
  features: [
    {
      icon: CheckCircle,
      title: 'Professional Quality',
      description: 'Industry-standard content that drives results.',
    },
    {
      icon: Clock,
      title: 'Lightning Fast',
      description: 'Get professional content in minutes, not hours.',
    },
    {
      icon: TargetIcon,
      title: 'Conversion Optimized',
      description: 'Content designed to achieve your business goals.',
    },
  ],
  useCases: [
    {
      icon: UserIcon,
      title: 'Small Businesses',
      description: 'Perfect for entrepreneurs and small business owners.',
    },
    {
      icon: BriefcaseIcon,
      title: 'Marketing Teams',
      description: 'Ideal for marketing professionals and agencies.',
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Optimized for international markets and audiences.',
    },
  ],
  websitePromo: {
    title: 'Transform your content strategy with AI',
    description:
      'Professional content that engages audiences and drives business growth.',
    features: [
      {
        emoji: '⚡',
        title: 'Fast Generation',
        description: 'Create content in minutes',
      },
      {
        emoji: '🎯',
        title: 'Targeted Content',
        description: 'Optimized for your audience',
      },
      {
        emoji: '📈',
        title: 'Proven Results',
        description: 'Content that drives conversions',
      },
    ],
  },
  geoExamples: [
    {
      country: 'United States',
      badgeColor: 'bg-blue-500',
      title: 'US Businesses',
      description: 'Optimized for US markets with local SEO and compliance.',
    },
    {
      country: 'United Kingdom',
      badgeColor: 'bg-purple-500',
      title: 'UK Businesses',
      description:
        'Tailored for UK markets with proper formatting and regulations.',
    },
    {
      country: 'Singapore & Asia',
      badgeColor: 'bg-amber-500',
      title: 'Singapore & Asia',
      description:
        'Built for Asian markets with multi-language and cultural adaptation.',
    },
  ],
  faqs: [
    {
      question: 'How accurate is the AI content generation?',
      answer:
        'Our AI creates professional-quality content with 95% accuracy. It understands industry context, tone, and best practices. All content is reviewed and can be easily edited to match your brand voice perfectly.',
    },
    {
      question: 'Can I customize the generated content?',
      answer:
        'Yes, full customization is available. Edit any generated content, adjust tone, add your brand voice, or completely rewrite sections. The AI provides a strong starting point that you can refine.',
    },
    {
      question: 'What types of content can the AI generate?',
      answer:
        'The AI can create articles, social media posts, marketing copy, website content, email sequences, product descriptions, and more. It adapts to different formats and industries automatically.',
    },
    {
      question: 'How long does content generation take?',
      answer:
        'Content is generated in seconds to minutes depending on length and complexity. Short social posts take 5 seconds, while comprehensive articles take 1-2 minutes. Much faster than writing from scratch.',
    },
    {
      question: 'Is the AI content original and plagiarism-free?',
      answer:
        'Yes, all content is 100% original. Our AI generates unique content based on your inputs and industry best practices. No plagiarism concerns or duplicate content issues.',
    },
    {
      question: 'Can the AI handle industry-specific terminology?',
      answer:
        'Absolutely. The AI is trained on industry-specific language and understands technical terms, jargon, and professional terminology for your field. It adapts content to your industry automatically.',
    },
    {
      question: 'What if I need content in multiple languages?',
      answer:
        'The AI can generate content in multiple languages including English, Spanish, French, German, Chinese, and more. Professional translations that maintain cultural context and local nuances.',
    },
    {
      question: 'How do I ensure brand consistency?',
      answer:
        'Provide brand guidelines and examples. The AI learns your brand voice, tone, and style preferences. Consistent messaging across all generated content maintains your brand identity.',
    },
  ],
  cta: {
    title: 'Ready to create professional content?',
    description:
      'Join thousands of businesses using HeyBoss AI to create content that drives results.',
  },
  relatedTools: [
    {
      icon: FileTextIcon,
      title: 'Business Plan Generator',
      description: 'Create comprehensive business plans',
      href: '/tool/ai-business-plan-generator',
      iconColor: 'bg-indigo-500',
    },
    {
      icon: BriefcaseIcon,
      title: 'Resume Generator',
      description: 'Build professional resumes',
      href: '/tool/ai-resume-generator',
      iconColor: 'bg-emerald-500',
    },
  ],
}
